import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../services/api";
import useAuthStore from "../store/authStore";
import { Button } from "../components/ui/button";
import { Loader2, MapPin, Package, Clock, Zap, AlertTriangle, IndianRupee, CheckCircle2, Lock, Truck, Warehouse } from "lucide-react";

const POLL_MS = 8000;

export default function AgentDispatchFeed() {
  const { user } = useAuthStore();
  const role = user?.role || "user";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);

  const fetchFeed = async () => {
    try {
      const { data } = await api.get("/dispatch/feed");
      setData(data);
    } catch (err) {
      if (err.response?.status === 403) toast.error("Agents only");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (role !== "agent") { setLoading(false); return; }
    fetchFeed();
    const id = setInterval(fetchFeed, POLL_MS);
    return () => clearInterval(id);
  }, [role]);

  const accept = async (tid) => {
    setAccepting(tid);
    try {
      await api.post("/dispatch/accept", { tracking_id: tid });
      toast.success("Accepted! The order is yours.");
      fetchFeed();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't accept");
      fetchFeed();
    } finally { setAccepting(null); }
  };

  if (loading) return <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>;

  if (role !== "agent") {
    return (
      <div className="p-10 bg-slate-50 border border-slate-200 rounded-2xl text-center max-w-xl mx-auto" data-testid="dispatch-access-denied">
        <Lock className="h-12 w-12 text-slate-400 mx-auto mb-3" />
        <h2 className="font-['Outfit'] text-xl font-bold text-slate-900 mb-1">Agents-only area</h2>
        <p className="text-sm text-slate-500">The Live Job Feed is only visible to approved gig-agents. Sign in with an agent account to accept dispatch offers.</p>
      </div>
    );
  }

  if (data?.suspended) {
    const until = data.suspended_until ? new Date(data.suspended_until).toLocaleString() : "—";
    return (
      <div className="p-10 bg-red-50 border border-red-200 rounded-2xl text-center" data-testid="suspended-state">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-3" />
        <h2 className="font-['Outfit'] text-xl font-bold text-red-900 mb-1">Account Suspended</h2>
        <p className="text-sm text-red-700 mb-2">{data.suspended_reason}</p>
        <p className="text-xs text-red-500">Resumes at: {until}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agent-dispatch-feed">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Zap className="h-6 w-6 text-[#FF6B00]" /> Live Job Offers
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            First to accept wins · {data?.accept_window_seconds ?? 30}s window
            {data?.throttled && <span className="ml-2 text-[11px] text-amber-600 font-semibold">⚠ Low rating — jobs shown with a delay</span>}
          </p>
        </div>
        <div className="px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs text-emerald-700 font-semibold">
          Rating: {data?.rating ?? "—"}
        </div>
      </div>

      {(!data?.offers || data.offers.length === 0) ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center" data-testid="empty-feed">
          <Package className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No live offers right now. Stay online — we'll notify you when a job comes in.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {data.offers.map((o) => {
            const isLastMile = o.leg === "last";
            const acceptLabel = isLastMile ? "Accept last-mile delivery" : "Accept Offer";
            const cardBorder = isLastMile ? "border-indigo-300" : "border-[#FF6B00]/40";
            const idColor = isLastMile ? "text-indigo-600" : "text-[#FF6B00]";
            return (
              <div
                key={`${o.tracking_id}-${o.leg || "first"}`}
                className={`bg-white rounded-2xl border-2 ${cardBorder} p-5 shadow-sm hover:shadow-lg transition`}
                data-testid={`offer-${o.tracking_id}`}
                data-leg={o.leg || "first"}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-mono text-xs font-bold ${idColor}`}>{o.tracking_id}</span>
                  {o.price_total && <span className="font-['Outfit'] text-lg font-bold text-slate-900 flex items-center"><IndianRupee className="h-4 w-4" />{o.price_total}</span>}
                </div>
                <div className="mb-3">
                  {isLastMile ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid="leg-badge-last">
                      <Warehouse className="h-3 w-3" /> Last mile · pickup from hub
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200" data-testid="leg-badge-first">
                      <Truck className="h-3 w-3" /> First mile · pickup from sender
                    </span>
                  )}
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex gap-2"><MapPin className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0 mt-0.5" /><span className="text-slate-700 line-clamp-2"><span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Pickup</span><br />{o.pickup_address}</span></div>
                  <div className="flex gap-2"><MapPin className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" /><span className="text-slate-700 line-clamp-2"><span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Deliver</span><br />{o.delivery_address}</span></div>
                  <div className="flex items-center gap-2 text-slate-500 pt-1 border-t border-slate-100"><Clock className="h-3 w-3" /> Broadcast: {new Date(o.broadcast_at).toLocaleTimeString()}</div>
                </div>
                <Button
                  onClick={() => accept(o.tracking_id)}
                  disabled={accepting === o.tracking_id}
                  className={`w-full mt-4 rounded-xl h-10 ${isLastMile ? "bg-indigo-600 hover:bg-indigo-700" : "bg-[#FF6B00] hover:bg-[#E65A00]"}`}
                  data-testid={`accept-${o.tracking_id}`}
                >
                  {accepting === o.tracking_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> {acceptLabel}</>}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
